import { useQuery } from '@tanstack/react-query'
import { BadgeDollarSign } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  EmptyState,
  HelpHint,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { fetchCosts, type CostSnapshotType } from '../lib/api.ts'

/**
 * Atendimento → Custos (F054): a conta no fim do mês.
 *
 * Soma o que já é gravado por mensagem desde a F046
 * (`communication_message.cost_micros_usd`), fechado por mês e categoria.
 * **Hoje o custo é sempre zero** — a Meta só publica as tarifas do rework
 * de cobrança até 01/09/2026, e sem uma tarifa não há multiplicação a
 * fazer. O que esta tela garante é a FORMA: volume por categoria e por
 * período, pronta para o dia em que a comparação antes/depois de outubro
 * de 2026 precisar ser lida de relance.
 *
 * `metaReportedCostMicrosUsd`/`divergenceMicrosUsd` ficam nulos até existir
 * uma integração com `GET /{waba-id}/pricing_analytics` da Meta — não
 * escondido, dito na própria coluna ("não conferido ainda").
 */

const formatUsdMicros = (micros: string): string => {
  const value = Number(BigInt(micros)) / 1_000_000
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

/**
 * A coluna de custo, honesta.
 *
 * `costMicrosUsd` soma `communication_message.cost_micros_usd` — e NADA no
 * repositório grava essa coluna: os webhooks de status da Meta trazem só
 * `pricing_model`/`type`/`category`, nunca o preço (a tarifa vem de uma
 * tabela que ainda não existe, e a Meta só publica as novas até 01/09/2026).
 * A soma é sempre "0" hoje, e mostrar "$0.00" seria uma MENTIRA — pareceria
 * "não custou nada" quando a verdade é "não sabemos quanto custou". Um
 * fechamento com mensagem de verdade (`messageCount > 0`) e custo exatamente
 * zero é o sinal de que não há tarifa cadastrada para este bucket; a
 * contagem e a categoria continuam reais e aparecem do mesmo jeito.
 */
const CostCell = ({ row }: { row: CostSnapshotType }): ReactNode => {
  if (row.costMicrosUsd === '0') {
    return (
      <span className="flex items-center gap-1.5 text-(--text-muted) italic">
        tarifa não cadastrada
        <HelpHint label="tarifa não cadastrada">
          A Meta informa a CATEGORIA de cada mensagem (Marketing, Utilidade,
          Autenticação), mas não o preço — o preço vem de uma tabela de tarifas
          que ainda não existe no Aeris. Ela entra quando a Meta publicar as
          tarifas do rework de cobrança de 01/10/2026 (previsto até 01/09/2026).
          A contagem de mensagens por categoria já é confiável hoje.
        </HelpHint>
      </span>
    )
  }
  return <>{formatUsdMicros(row.costMicrosUsd)}</>
}

const periodLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })

const CATEGORY_LABEL: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação',
  unknown: 'Sem categoria',
}

export const AtendimentoCustosPage = (): ReactNode => {
  const costs = useQuery({
    queryKey: ['communication-costs'],
    queryFn: async ({ signal }) => await fetchCosts({}, signal),
  })

  const rows = costs.data ?? []
  const hasAnyMetaData = rows.some(
    (row) => row.metaReportedCostMicrosUsd !== null
  )

  return (
    <>
      <TabHeader
        title="Custos de atendimento"
        description="O que a loja gastou em mensagens, por categoria e por período — hoje sempre zero, porque a Meta ainda não publicou as tarifas do rework de cobrança de 01/10/2026."
      />

      {!hasAnyMetaData && rows.length > 0 ? (
        <div className="mb-4 rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) p-3 text-(length:--text-sm) text-(--text-muted)">
          Nenhum período foi conferido com o relatório da Meta ainda — a coluna
          &quot;Meta relatou&quot; fica em branco até essa integração existir. O
          número que a Meta reportar é o que vira fatura.
        </div>
      ) : null}

      {costs.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : costs.isError ? (
        <EmptyState
          icon={<BadgeDollarSign aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar os custos"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BadgeDollarSign aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma mensagem de saída nos últimos meses"
          description="O fechamento aparece aqui assim que a loja enviar a primeira mensagem no período."
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Período</TableHeaderCell>
                <TableHeaderCell>Categoria</TableHeaderCell>
                <TableHeaderCell>Tipo</TableHeaderCell>
                <TableHeaderCell>Mensagens</TableHeaderCell>
                <TableHeaderCell>Custo (Aeris)</TableHeaderCell>
                <TableHeaderCell>Meta relatou</TableHeaderCell>
                <TableHeaderCell>Divergência</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={`${row.periodStart}-${row.pricingCategory}-${row.pricingType}-${String(index)}`}
                >
                  <TableCell>{periodLabel(row.periodStart)}</TableCell>
                  <TableCell>
                    {CATEGORY_LABEL[row.pricingCategory] ?? row.pricingCategory}
                  </TableCell>
                  <TableCell>{row.pricingType}</TableCell>
                  <TableCell>{row.messageCount}</TableCell>
                  <TableCell>
                    <CostCell row={row} />
                  </TableCell>
                  <TableCell>
                    {row.metaReportedCostMicrosUsd === null
                      ? 'não conferido'
                      : formatUsdMicros(row.metaReportedCostMicrosUsd)}
                  </TableCell>
                  <TableCell>
                    {row.divergenceMicrosUsd === null
                      ? '—'
                      : formatUsdMicros(row.divergenceMicrosUsd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
